import React, { useEffect, useRef, useState } from 'react';
import ButtonWithLoading from '../common/ButtomWIthLoading';
import DisplayInfoMessage from '../common/DisplayInfoMessage';
import { AUTH_UI_TEXT, AUTH_TIMER } from '../../constants/auth.constants';

interface OtpProps {
  email: string;
  onVerify: (otp: string) => void;
  onResend: (email: string) => void;
  onBackToLogin: () => void;
  isLoading?: boolean;
  isResendLoading?: boolean;
  displayMessage?: { error: boolean; message: string };
}

const Otp: React.FC<OtpProps> = ({
  email,
  onVerify,
  onResend,
  onBackToLogin,
  isLoading = false,
  isResendLoading = false,
  displayMessage = { error: false, message: '' },
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(AUTH_TIMER.OTP_COUNTDOWN_SECONDS);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Get client type for dynamic styling
  const clientType = process.env.REACT_APP_CLIENT;
  const primaryColor = clientType === 'salesbot2290ai' ? '#16A34A' : '#114175';
  const hoverColor = clientType === 'salesbot2290ai' ? '#15803D' : '#6491C3';
  const focusBorderColor = clientType === 'salesbot2290ai' ? 'focus:border-[#16A34A]' : 'focus:border-[#114175]';
  const textColor = clientType === 'salesbot2290ai' ? 'text-[#16A34A]' : 'text-[#114175]';

  // Focus first OTP input on mount
  useEffect(() => {
    document.getElementById('otp-0')?.focus();
    startTimer();

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Timer logic
  const startTimer = () => {
    setCountdown(AUTH_TIMER.OTP_COUNTDOWN_SECONDS);
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleOtpInputChange = (index: number, value: string) => {
    if (value.length > 1 || !/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = pastedData.padEnd(6, '').split('');
    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex((digit) => digit === '');
    const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : 5;
    document.getElementById(`otp-${focusIndex}`)?.focus();
  };

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      const otpCode = otp.join('');
      if (otpCode.length === 6) handleVerify();
      return;
    }

    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleVerify = () => {
    const otpCode = otp.join('');
    if (otpCode.length === 6) onVerify(otpCode);
  };

  const handleResend = () => {
    onResend(email);
    setOtp(['', '', '', '', '', '']);
    startTimer();
  };

  const isOtpComplete = otp.join('').length === 6;

  return (
    <div className="p-4 sm:p-6 max-w-lg mx-auto w-full">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-6">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{AUTH_UI_TEXT.OTP_TITLE}</h3>
        <p className="text-sm text-gray-600">{AUTH_UI_TEXT.OTP_DESCRIPTION}</p>
        <p className="text-sm font-semibold text-gray-900 mt-1">{email}</p>
      </div>

      {/* OTP Input Fields */}
      <div className="flex justify-center gap-2 sm:gap-3 mb-4">
        {otp.map((digit, index) => (
          <input
            key={index}
            id={`otp-${index}`}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpInputChange(index, e.target.value)}
            onKeyDown={(e) => handleOtpKeyDown(index, e)}
            onPaste={handlePaste}
            className={`w-10 h-10 sm:w-12 sm:h-12 text-center text-lg font-semibold border-2 border-gray-300 rounded-lg ${focusBorderColor} focus:outline-none focus:ring-2 ${
              clientType === 'salesbot2290ai' ? 'focus:ring-[#16A34A]' : 'focus:ring-[#114175]'
            } transition-all ${isLoading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
            disabled={isLoading}
          />
        ))}
      </div>

      {/* Error/Success Message */}
      <DisplayInfoMessage displayMessage={displayMessage} />

      {/* Verify Button */}
      <ButtonWithLoading
        isLoading={isLoading}
        label={AUTH_UI_TEXT.OTP_BUTTON}
        type="button"
        className={`w-full py-3 rounded-full text-white font-semibold text-base transition-colors ${
          isOtpComplete && !isLoading
            ? 'cursor-pointer'
            : 'bg-gray-300 cursor-not-allowed'
        }`}
        style={isOtpComplete && !isLoading ? { backgroundColor: primaryColor } : {}}
        onMouseEnter={(e: any) => {
          if (isOtpComplete && !isLoading) {
            e.currentTarget.style.backgroundColor = hoverColor;
          }
        }}
        onMouseLeave={(e: any) => {
          if (isOtpComplete && !isLoading) {
            e.currentTarget.style.backgroundColor = primaryColor;
          }
        }}
        disabled={!isOtpComplete || isLoading}
        onClick={handleVerify}
      />

      {/* Resend OTP / Timer */}
      <div className="text-center mt-4 sm:mt-6">
        {countdown > 0 ? (
          <p className="text-sm text-gray-600">
            {AUTH_UI_TEXT.OTP_RESEND_TEXT}{' '}
            <span className="font-semibold text-gray-900">
              {AUTH_TIMER.OTP_RESEND_FORMAT(countdown)}
            </span>
          </p>
        ) : (
          <p className="text-sm text-gray-600">
            {AUTH_UI_TEXT.OTP_RESEND_TEXT}{' '}
            <button
              type="button"
              className={`${textColor} hover:underline font-semibold`}
              onClick={handleResend}
              disabled={isResendLoading}
            >
              {isResendLoading ? AUTH_UI_TEXT.OTP_RESENDING : AUTH_UI_TEXT.OTP_RESEND_LINK}
            </button>
          </p>
        )}
      </div>

      {/* Back to Login */}
      <div className="text-center mt-4">
        <button
          type="button"
          className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
          onClick={onBackToLogin}
        >
          {AUTH_UI_TEXT.OTP_BACK_TO_SIGNUP}
        </button>
      </div>
    </div>
  );
};

export default Otp;

